/**
 * DailySheetHistoryPage — owner-only list of past scanned daily sheets.
 *
 * Shows a simple date-sorted table with an "Open" and "Delete" per row.
 * Delete pops a confirm dialog with an optional "undo routed inserts"
 * checkbox (default OFF — we don't want a single misclick to wipe a
 * day's expenses).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Trash2, ScanText, Eye, Download, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '../lib/api';

// Trigger a browser download for an in-memory Blob. Shared with the per-day
// row action so we don't repeat the anchor-click ritual twice on this page.
function triggerBlobDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Give the browser a beat to flush the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 500);
}

export default function DailySheetHistoryPage() {
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [pendingDelete, setPendingDelete] = useState(null); // {id, date}
    const [undoRouted, setUndoRouted] = useState(false);
    // Sprint 2026-07: month picker for the "Download month" button.
    // Defaults to the current month; keeps the user in control when they
    // want an older month's export.
    const currentMonth = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }, []);
    const [exportMonth, setExportMonth] = useState(currentMonth);
    const [downloadingMonth, setDownloadingMonth] = useState(false);
    const [downloadingDate, setDownloadingDate] = useState(null); // yyyy-mm-dd
    // Phase 8 — one-time (idempotent) backfill of vouchers from historical sheets.
    const [backfilling, setBackfilling] = useState(false);

    async function handleBackfillVouchers() {
        // Two-step UX: dry-run preview → confirm → real backfill.
        setBackfilling(true);
        try {
            const preview = await api.backfillVouchersFromDailySheets({ dry_run: true });
            if (preview.sheets_processed === 0) {
                toast.info('No historical sheets to backfill — all already have vouchers, or the history is empty.');
                return;
            }
            const disallowedNote = preview.section_40a3_disallowed_total > 0
                ? `\n\n⚠ ₹${preview.section_40a3_disallowed_total.toLocaleString('en-IN')} will be tagged as Section 40A(3) disallowed (cash > ₹10k/day/category).`
                : '';
            const proceed = window.confirm(
                `Convert ${preview.sheets_processed} historical daily sheet(s) into accounting vouchers?\n\n`
                + `• ${preview.vouchers_created} voucher(s) will be created\n`
                + `• All rows assumed CASH (historical data has no payment mode)\n`
                + `• No stock ledger entries (historical rows have no unit price)\n`
                + `• Already-converted sheets skipped (safe to run again)`
                + disallowedNote
                + `\n\nProceed?`,
            );
            if (!proceed) return;
            const res = await api.backfillVouchersFromDailySheets({ dry_run: false });
            toast.success(
                `Backfilled ${res.sheets_processed} sheet(s) · ${res.vouchers_created} voucher(s) created`
                + (res.section_40a3_disallowed_total > 0
                    ? ` · ⚠ ₹${res.section_40a3_disallowed_total.toLocaleString('en-IN')} disallowed (40A(3))`
                    : ''),
                { duration: 8000 },
            );
            if (res.errors?.length) {
                toast.warning(`${res.errors.length} sheet(s) had errors — check the browser console.`);
                console.warn('Voucher backfill errors:', res.errors);
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Backfill failed.');
        } finally {
            setBackfilling(false);
        }
    }

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.dsList(60);
            setItems(r?.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const doDelete = async () => {
        if (!pendingDelete) return;
        try {
            const res = await api.dsDelete(pendingDelete.id, undoRouted);
            const parts = [`Deleted sheet ${pendingDelete.date}`];
            if (res?.vouchers_deleted) parts.push(`${res.vouchers_deleted} voucher(s)`);
            if (res?.stock_ledger_deleted) parts.push(`${res.stock_ledger_deleted} stock entry(ies) reversed`);
            if (undoRouted) parts.push('routed data reversed');
            toast.success(parts.join(' · '));
            setPendingDelete(null);
            setUndoRouted(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Delete failed');
        }
    };

    // Download the 7-tab Shakewallah-style workbook for the picked month.
    const downloadMonth = async () => {
        if (!/^\d{4}-\d{2}$/.test(exportMonth || '')) {
            toast.error('Pick a valid month first');
            return;
        }
        setDownloadingMonth(true);
        try {
            const { blob, filename } = await api.dsExport({ month: exportMonth });
            triggerBlobDownload(blob, filename);
            toast.success(`Downloaded ${filename}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            // Blob error responses need to be parsed as text; axios gives us
            // a Blob body when responseType='blob'.
            if (!detail && e?.response?.data instanceof Blob) {
                try {
                    const text = await e.response.data.text();
                    const j = JSON.parse(text);
                    toast.error(j?.detail || 'Download failed');
                } catch {
                    toast.error('Download failed');
                }
            } else {
                toast.error(detail || 'Download failed');
            }
        } finally {
            setDownloadingMonth(false);
        }
    };

    // Download the workbook for a single day (single row's date).
    const downloadDay = async (date) => {
        setDownloadingDate(date);
        try {
            const { blob, filename } = await api.dsExport({ date });
            triggerBlobDownload(blob, filename);
            toast.success(`Downloaded ${filename}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (!detail && e?.response?.data instanceof Blob) {
                try {
                    const text = await e.response.data.text();
                    const j = JSON.parse(text);
                    toast.error(j?.detail || 'Download failed');
                } catch {
                    toast.error('Download failed');
                }
            } else {
                toast.error(detail || 'Download failed');
            }
        } finally {
            setDownloadingDate(null);
        }
    };

    return (
        <div data-testid="daily-sheet-history-page" className="p-4 md:p-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h1 className="font-heading text-2xl flex items-center gap-2">
                        <ScanText size={22} className="text-primary" /> Daily Sheet History
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Every sheet you've scanned, newest first.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Input
                        type="month"
                        value={exportMonth}
                        onChange={(e) => setExportMonth(e.target.value)}
                        className="h-9 w-[160px]"
                        data-testid="dsh-export-month-input"
                        aria-label="Month for Excel export"
                    />
                    <Button
                        variant="secondary"
                        onClick={downloadMonth}
                        disabled={downloadingMonth}
                        data-testid="dsh-download-month-btn"
                        className="gap-2"
                    >
                        {downloadingMonth ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <Download size={16} />
                        )}
                        Download month
                    </Button>
                    <Button onClick={() => nav('/daily-sheet')} data-testid="dsh-new-scan-btn">
                        New scan
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleBackfillVouchers}
                        disabled={backfilling}
                        className="gap-1"
                        data-testid="dsh-backfill-vouchers-btn"
                        title="Convert every past daily sheet into accounting vouchers (Section 40A(3) rules applied). Safe to run multiple times."
                    >
                        {backfilling ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
                        Backfill vouchers
                    </Button>
                </div>
            </div>

            <Card className="overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-muted-foreground">
                        <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-10 text-center">
                        <p className="text-sm text-muted-foreground mb-4">
                            You haven't scanned any daily sheet yet.
                        </p>
                        <Button onClick={() => nav('/daily-sheet')}>Scan your first sheet</Button>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Cash In</TableHead>
                                <TableHead className="text-right">Cash Out</TableHead>
                                <TableHead className="text-right">Expenses</TableHead>
                                <TableHead className="text-right">Advances</TableHead>
                                <TableHead className="text-right">Absent</TableHead>
                                <TableHead className="w-24" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((row) => {
                                const expensesTotal = (row.expenses || []).reduce(
                                    (a, b) => a + (Number(b.amount) || 0),
                                    0
                                );
                                const advancesTotal = (row.advances || []).reduce(
                                    (a, b) => a + (Number(b.amount) || 0),
                                    0
                                );
                                return (
                                    <TableRow key={row.id} data-testid={`dsh-row-${row.date}`}>
                                        <TableCell className="font-medium">{row.date}</TableCell>
                                        <TableCell className="text-right font-mono">
                                            ₹{Number(row.cash_in?.total || 0).toLocaleString('en-IN')}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            ₹{Number(row.cash_out?.total || 0).toLocaleString('en-IN')}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            ₹{expensesTotal.toLocaleString('en-IN')}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            ₹{advancesTotal.toLocaleString('en-IN')}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {(row.absent || []).length ? (
                                                <Badge variant="secondary">{(row.absent || []).length}</Badge>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1 justify-end">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => downloadDay(row.date)}
                                                    disabled={downloadingDate === row.date}
                                                    data-testid={`dsh-download-${row.date}`}
                                                    title="Download this day as Excel"
                                                >
                                                    {downloadingDate === row.date ? (
                                                        <Loader2 size={14} className="animate-spin" />
                                                    ) : (
                                                        <Download size={14} />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => nav(`/daily-sheet?open=${encodeURIComponent(row.id)}`)}
                                                    data-testid={`dsh-view-${row.date}`}
                                                    title="Open & edit this sheet"
                                                >
                                                    <Eye size={14} />
                                                </Button>
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-destructive"
                                                            onClick={() =>
                                                                setPendingDelete({ id: row.id, date: row.date })
                                                            }
                                                            data-testid={`dsh-delete-${row.date}`}
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>
                                                                Delete daily sheet {pendingDelete?.date}?
                                                            </AlertDialogTitle>
                                                            <AlertDialogDescription asChild>
                                                                <div className="text-sm text-muted-foreground">
                                                                    Choose what gets removed. This action cannot be undone.
                                                                </div>
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>

                                                        {/* Always-deleted block — makes voucher + stock cascade explicit */}
                                                        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs space-y-1">
                                                            <div className="font-medium text-foreground">Will be removed automatically:</div>
                                                            <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                                                                <li>The daily sheet record for {pendingDelete?.date}</li>
                                                                <li>Any <span className="font-medium text-foreground">accounting vouchers</span> generated from this sheet</li>
                                                                <li>Any <span className="font-medium text-foreground">stock ledger entries</span> those vouchers created</li>
                                                            </ul>
                                                        </div>

                                                        <div className="flex items-start gap-2 py-2">
                                                            <Checkbox
                                                                id="undo-routed-check"
                                                                checked={undoRouted}
                                                                onCheckedChange={(v) => setUndoRouted(!!v)}
                                                                data-testid="dsh-undo-routed-check"
                                                                className="mt-0.5"
                                                            />
                                                            <label htmlFor="undo-routed-check" className="text-sm leading-snug">
                                                                Also delete the routed <span className="font-medium">expenses, advances and Vijay&nbsp;Ras</span> entries created from this sheet
                                                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                                                    Off by default — leaves your Expenses page untouched.
                                                                </div>
                                                            </label>
                                                        </div>

                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel
                                                                onClick={() => {
                                                                    setPendingDelete(null);
                                                                    setUndoRouted(false);
                                                                }}
                                                            >
                                                                Cancel
                                                            </AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={doDelete}
                                                                data-testid="dsh-delete-confirm"
                                                            >
                                                                Delete
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </div>
    );
}
